import { useState } from "react";
import { budgetItems, monthlySummary } from "../../../mocks/finance";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

export default function BudgetTable() {
  const [showChart, setShowChart] = useState(false);

  const getStatusStyle = (status: string) => {
    if (status === "위험") return "bg-[#E8375F]/10 text-[#E8375F] border-[#E8375F]/30";
    if (status === "경고") return "bg-[#FF9F43]/10 text-[#FF9F43] border-[#FF9F43]/30";
    return "bg-[#00C896]/10 text-[#00C896] border-[#00C896]/30";
  };

  const getBarColor = (status: string) => {
    if (status === "위험") return "#E8375F";
    if (status === "경고") return "#FF9F43";
    return "#00C896";
  };

  const totalAllocated = budgetItems.reduce((s, b) => s + b.allocated, 0);
  const totalSpent = budgetItems.reduce((s, b) => s + b.spent, 0);
  const usageRate = Math.round((totalSpent / totalAllocated) * 100);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] p-4">
          <div className="text-[#5A5E72] text-xs mb-1">총 배정 예산</div>
          <div className="text-[#111111] font-bold text-lg">{totalAllocated.toLocaleString()}만</div>
        </div>
        <div className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] p-4">
          <div className="text-[#5A5E72] text-xs mb-1">집행 금액</div>
          <div className="text-[#FF9F43] font-bold text-lg">{totalSpent.toLocaleString()}만</div>
        </div>
        <div className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] p-4">
          <div className="text-[#5A5E72] text-xs mb-1">예산 소진율</div>
          <div className={`font-bold text-lg ${usageRate > 80 ? "text-[#E8375F]" : usageRate > 60 ? "text-[#FF9F43]" : "text-[#00C896]"}`}>
            {usageRate}%
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center justify-between">
        <span className="text-[#8B91A8] text-sm font-semibold">부서별 예산 현황</span>
        <div className="flex items-center gap-1 bg-[#252830] rounded-lg p-1">
          <button type="button" onClick={() => setShowChart(false)} className={`px-3 py-1 rounded-md text-xs transition-all cursor-pointer whitespace-nowrap ${!showChart ? "bg-[#1C1F2E] text-[#111111]" : "text-[#5A5E72]"}`}>
            표 보기
          </button>
          <button type="button" onClick={() => setShowChart(true)} className={`px-3 py-1 rounded-md text-xs transition-all cursor-pointer whitespace-nowrap ${showChart ? "bg-[#1C1F2E] text-[#111111]" : "text-[#5A5E72]"}`}>
            차트 보기
          </button>
        </div>
      </div>

      {showChart ? (
        <div className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] p-4">
          <div className="text-[#5A5E72] text-xs mb-4">월별 손익 현황 (만원)</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthlySummary}>
              <CartesianGrid strokeDasharray="3 3" stroke="#252830" />
              <XAxis dataKey="month" tick={{ fill: "#5A5E72", fontSize: 11 }} />
              <YAxis tick={{ fill: "#5A5E72", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#1C1F2E", border: "1px solid #2A2D3E", borderRadius: 8 }} />
              <Legend />
              <Bar dataKey="income" name="수입" fill="#00C896" radius={[3,3,0,0]} />
              <Bar dataKey="expense" name="지출" fill="#E8375F" radius={[3,3,0,0]} />
              <Bar dataKey="profit" name="순이익" fill="#FF9F43" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#252830]">
                {["부서", "항목", "배정", "집행", "잔여", "진행도", "상태"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[#5A5E72] font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#252830]">
              {budgetItems.map((b) => {
                const pct = Math.round((b.spent / b.allocated) * 100);
                return (
                  <tr key={b.id} className="hover:bg-[#252830]/50 transition-colors">
                    <td className="px-4 py-3 text-[#8B91A8]">{b.dept}</td>
                    <td className="px-4 py-3 text-[#111111] font-medium">{b.category}</td>
                    <td className="px-4 py-3 text-[#111111]">{b.allocated.toLocaleString()}만</td>
                    <td className="px-4 py-3 text-[#FF9F43]">{b.spent.toLocaleString()}만</td>
                    <td className="px-4 py-3 text-[#00C896]">{b.remaining.toLocaleString()}만</td>
                    <td className="px-4 py-3 w-32">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-[#252830] rounded-full h-1.5">
                          <div
                            className="h-1.5 rounded-full transition-all"
                            style={{ width: `${pct}%`, backgroundColor: getBarColor(b.status) }}
                          />
                        </div>
                        <span className="text-[#5A5E72] text-[10px] w-8 text-right">{pct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${getStatusStyle(b.status)}`}>
                        {b.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
